import React from 'react';
import { Phone, Smartphone, Mail, MapPin, Shield, ExternalLink } from 'lucide-react';
import { companyInfo, certifications } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Services', href: '#services' },
    { name: 'Our Process', href: '#process' },
    { name: 'Why Choose Us', href: '#why-us' },
    { name: 'Contact Us', href: '#contact' },
  ];

  const services = [
    'Bed Bug Control',
    'Rodent Control',
    'Insect Control',
    'Wasp Treatment',
    'Bird Control & Deterrents',
    'Commercial Pest Control',
    'Domestic Pest Control',
  ];

  return (
    <footer className="bg-white border-t border-neutral-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer */}
        <div className="py-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <img
              src={companyInfo.logo}
              alt={companyInfo.fullName}
              className="h-20 w-auto mb-6 rounded-xl"
            />
            <p className="text-neutral-600 mb-6 leading-relaxed">
              Professional pest control services across Lincolnshire. Specialists
              in bed bug elimination with {companyInfo.experience} years of trusted experience.
            </p>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-brand" />
              <span className="text-neutral-700 text-sm">{companyInfo.tagline}</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-neutral-900 font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-neutral-600 hover:text-brand transition-colors duration-200"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-neutral-900 font-semibold mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-neutral-600">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-neutral-900 font-semibold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${companyInfo.phoneTel}`}
                  className="flex items-center gap-3 text-neutral-600 hover:text-brand transition-colors group"
                >
                  <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center group-hover:bg-brand/20 transition-colors">
                    <Phone className="w-5 h-5" />
                  </div>
                  <span>{companyInfo.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`tel:${companyInfo.mobileTel}`}
                  className="flex items-center gap-3 text-neutral-600 hover:text-brand transition-colors group"
                >
                  <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center group-hover:bg-brand/20 transition-colors">
                    <Smartphone className="w-5 h-5" />
                  </div>
                  <span>{companyInfo.mobile}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-3 text-neutral-600 hover:text-brand transition-colors group"
                >
                  <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center group-hover:bg-brand/20 transition-colors">
                    <Mail className="w-5 h-5" />
                  </div>
                  <span className="break-all">{companyInfo.email}</span>
                </a>
              </li>
              <li>
                <div className="flex items-center gap-3 text-neutral-600">
                  <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <span>{companyInfo.serviceArea}</span>
                </div>
              </li>
            </ul>

          </div>
        </div>

        {/* Accredited Members - Full Width Centered */}
        <div className="py-8 border-t border-neutral-200">
          <p className="text-neutral-500 text-sm mb-4 text-center">Accredited Member:</p>
          <div className="flex justify-center items-center gap-6 flex-wrap">
            {certifications.map((cert) => (
              <div key={cert.name} className="bg-neutral-50 border border-neutral-200 rounded-md p-3 hover:scale-105 transition-transform duration-300">
                <img 
                  src={cert.logo} 
                  alt={cert.altText}
                  className="h-10 w-auto object-contain"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-neutral-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-neutral-500 text-sm text-center sm:text-left">
            © {currentYear} {companyInfo.fullName}. All rights reserved.
          </p>
          <a
            href={`https://${companyInfo.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-neutral-600 hover:text-brand transition-colors text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            {companyInfo.website}
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
